import React, { useEffect, useState, useMemo, useCallback } from 'react';
import * as api from '../../services/api';
import {
  Shield, ShieldCheck, AlertTriangle, Loader, Database, Lock, Eye,
  Building2, Zap, Globe, ExternalLink, Info, Sliders, Save, X,
  CheckCircle, History, RotateCcw,
} from 'lucide-react';

/**
 * WorkflowProtectionsPanel — read-only view of "what protections will fire when
 * this workflow runs": the auto-injected DPG nodes already in the canvas plus
 * the effective Data Classification Rules that scan their payloads.
 *
 * Powered by GET /api/v1/workflows/{id}/dpg-rules.
 */

const COLORS = {
  S: { 50: '#fafaf9', 100: '#f5f5f4', 150: '#eeeded', 200: '#e7e5e4', 300: '#d6d3d1', 400: '#a8a29e', 500: '#78716c', 600: '#57534e', 700: '#44403c', 800: '#292524', 900: '#1c1917' },
  brand: { primary: '#0d9488', light: '#e3f4f1', soft: '#f8d7d4' },
  emerald: { bg: '#ecfdf5', text: '#065f46', border: '#a7f3d0', icon: '#10b981' },
  amber:   { bg: '#fffbeb', text: '#92400e', border: '#fde68a', icon: '#f59e0b' },
  sky:     { bg: '#f0f9ff', text: '#0c4a6e', border: '#bae6fd', icon: '#0ea5e9' },
  rose:    { bg: '#fff1f2', text: '#9f1239', border: '#fecdd3', icon: '#f43f5e' },
  violet:  { bg: '#f5f3ff', text: '#5b21b6', border: '#ddd6fe', icon: '#8b5cf6' },
  slate:   { bg: '#f8fafc', text: '#475569', border: '#e2e8f0', icon: '#64748b' },
};

const CATEGORY_ICON = {
  PII: Eye, CREDENTIAL: Lock, FINANCIAL: Database, HEALTH: Shield,
  CORPORATE: Building2, CUSTOM: Zap,
};
const CATEGORY_ACCENT = {
  PII: 'violet', CREDENTIAL: 'rose', FINANCIAL: 'amber',
  HEALTH: 'sky', CORPORATE: 'emerald', CUSTOM: 'slate',
};
const ACTION_COLOR = {
  BLOCK: '#dc2626', REDACT: '#9f1239', TOKENIZE: '#5b21b6',
  MASK: '#92400e', LOG: '#475569',
};

const NODE_TYPE_LABEL = {
  dpg_scan_inbound: 'Inbound scan',
  dpg_scan_outbound: 'Outbound scan',
  dpg_internode_scan: 'Internode scan',
  dpg_scan: 'DPG scan',
  data_classification: 'Data classification',
  token_vault: 'Token vault',
  tokenize: 'Tokenize',
  vault_tokenize: 'Tokenize',
  vault_resolve: 'Detokenize',
  detokenize: 'Detokenize',
  policy_enforcement_gate: 'Policy enforcement',
  policy_enforcement: 'Policy enforcement',
  policy_audit: 'Policy audit',
  audit_record: 'Audit record',
  audit_event: 'Audit event',
  compliance_scoring: 'Compliance scoring',
  compliance_score: 'Compliance score',
  agent_autonomy_gate: 'Autonomy gate',
  policy_rule_evaluation: 'Policy rules (L1)',
  deterministic_rules: 'Deterministic rules',
  l1_rules: 'L1 rules',
};

const SYSTEM_TENANT_ID = '00000000-0000-0000-0000-000000000011';

export default function WorkflowProtectionsPanel({ workflowId, canManage = true }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [overrides, setOverrides] = useState(null);
  const [auditLog, setAuditLog] = useState([]);

  const loadAll = useCallback(async () => {
    if (!workflowId) return;
    setLoading(true);
    try {
      const [rulesRes, overridesRes, auditRes] = await Promise.all([
        api.getWorkflowDpgRules(workflowId),
        api.getWorkflowDpgOverrides(workflowId).catch(() => ({ ok: false })),
        api.getWorkflowDpgOverrideAudit(workflowId, 0, 25).catch(() => ({ ok: false })),
      ]);
      if (rulesRes.ok) {
        setData(rulesRes.data);
        setError(null);
      } else {
        setError(rulesRes.data?.message || 'Failed to load protection set');
      }
      if (overridesRes.ok) setOverrides(overridesRes.data);
      if (auditRes.ok) setAuditLog(auditRes.data?.content || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [workflowId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const grouped = useMemo(() => {
    const rules = data?.effectiveRules || [];
    const out = {};
    for (const r of rules) {
      out[r.category] = out[r.category] || [];
      out[r.category].push(r);
    }
    return out;
  }, [data]);

  if (loading) {
    return (
      <div style={{ padding: '60px 20px', textAlign: 'center', color: COLORS.S[500] }}>
        <Loader size={24} style={{ animation: 'wfpSpin 0.8s linear infinite' }} />
        <div style={{ marginTop: 12, fontSize: 13 }}>Loading protection set…</div>
        <style>{`@keyframes wfpSpin { from { transform: rotate(0); } to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }
  if (error) {
    return (
      <div style={{ padding: '14px 16px', borderRadius: 10,
        background: COLORS.rose.bg, border: `1px solid ${COLORS.rose.border}`,
        color: COLORS.rose.text, fontSize: 13, display: 'flex', alignItems: 'center', gap: 10 }}>
        <AlertTriangle size={16} color={COLORS.rose.icon} />
        {error}
      </div>
    );
  }
  if (!data) return null;

  const injected = data.injectedDpgNodes || [];
  const injectedByType = data.injectedByType || {};
  const rules = data.effectiveRules || [];
  const blockingCount = rules.filter(r => r.action === 'BLOCK').length;
  const disabledIds = new Set(overrides?.disabledRuleIds || []);
  const disabledCount = disabledIds.size;

  return (
    <div>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, rgba(226,74,58,0.04) 0%, rgba(197,58,43,0.02) 100%)',
        border: `1px solid ${COLORS.brand.soft}`, borderRadius: 12, padding: '16px 20px', marginBottom: 20,
        display: 'flex', alignItems: 'flex-start', gap: 12,
      }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#fff', border: `1px solid ${COLORS.brand.soft}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <ShieldCheck size={18} color={COLORS.brand.primary} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: COLORS.S[900], marginBottom: 4 }}>
            Data protections that will run on every execution
          </div>
          <div style={{ fontSize: 12, color: COLORS.S[600], lineHeight: 1.55 }}>
            This view shows the auto-injected DPG nodes already present in the canvas plus the
            tenant-scoped classification rules that will be evaluated by each scan. To edit the
            rules themselves go to{' '}
            <a href="/policy-config" style={{ color: COLORS.brand.primary, fontWeight: 600 }}>
              Policy Config → Data Classification <ExternalLink size={11} style={{ display: 'inline', verticalAlign: '-1px' }} />
            </a>.
          </div>
        </div>
      </div>

      {/* Stats row */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 14, marginBottom: 20 }}>
        <StatCard label="Injected DPG nodes" value={injected.length}
          sub={Object.entries(injectedByType).map(([t, n]) => `${NODE_TYPE_LABEL[t] || t}: ${n}`).join(' · ') || 'No protection nodes detected'}
          accent="violet" Icon={Shield} />
        <StatCard label="Active classification rules" value={rules.length}
          sub={`${Object.keys(grouped).length} categories represented`}
          accent="sky" Icon={Database} />
        <StatCard label="Blocking rules" value={blockingCount}
          sub={blockingCount > 0 ? 'Will reject any scan on match' : 'No hard-block rules active'}
          accent={blockingCount > 0 ? 'rose' : 'slate'} Icon={AlertTriangle} />
      </div>

      {/* Empty-canvas hint */}
      {injected.length === 0 && (
        <div style={{ marginBottom: 20, padding: '14px 16px', borderRadius: 10,
          background: COLORS.amber.bg, border: `1px solid ${COLORS.amber.border}`,
          color: COLORS.amber.text, fontSize: 13, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <Info size={16} color={COLORS.amber.icon} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <div style={{ fontWeight: 700, marginBottom: 2 }}>This workflow has no DPG nodes injected.</div>
            <div>Open the canvas and use <em>Inject Enforcement</em> in the toolbar to add inbound /
              outbound / internode scans. Without DPG nodes, the rules below will not run for this workflow.</div>
          </div>
        </div>
      )}

      {/* Injected nodes table */}
      {injected.length > 0 && (
        <Section title="Auto-injected DPG nodes" subtitle="Each node here applies the rule set below to data flowing through it.">
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 8 }}>
            {injected.map(node => (
              <div key={node.id} style={{
                background: '#fff', border: `1px solid ${COLORS.S[200]}`, borderRadius: 10,
                padding: '10px 12px', display: 'flex', alignItems: 'center', gap: 10,
              }}>
                <div style={{ width: 30, height: 30, borderRadius: 8, background: COLORS.violet.bg, border: `1px solid ${COLORS.violet.border}`,
                  display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Shield size={14} color={COLORS.violet.icon} />
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.S[900], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {NODE_TYPE_LABEL[node.type] || node.type}
                  </div>
                  <div style={{ fontSize: 10, color: COLORS.S[500], fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {node.id}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Customize protections — per-workflow DPG overrides */}
      <CustomizeProtectionsSection
        workflowId={workflowId}
        rules={rules}
        overrides={overrides}
        canManage={canManage}
        onSaved={loadAll}
      />

      {/* Effective rules grouped by category */}
      <Section title="Effective classification rules"
        subtitle={`${rules.length} rules sorted by priority then sensitivity. Rules are evaluated in this order; first BLOCK match short-circuits.`
                  + (disabledCount > 0 ? ` ${disabledCount} disabled by override.` : '')}>
        {rules.length === 0 ? (
          <div style={{ padding: 24, textAlign: 'center', color: COLORS.S[500],
            background: '#fff', border: `1px dashed ${COLORS.S[200]}`, borderRadius: 10 }}>
            <Database size={28} color={COLORS.S[300]} />
            <div style={{ marginTop: 10, fontSize: 13, fontWeight: 600 }}>No active rules for this tenant</div>
            <div style={{ fontSize: 12, marginTop: 4 }}>
              Add rules in <a href="/policy-config" style={{ color: COLORS.brand.primary, fontWeight: 600 }}>Policy Config → Data Classification</a> and they will apply to every workflow.
            </div>
          </div>
        ) : (
          Object.entries(grouped).map(([category, list]) => (
            <CategoryGroup key={category} category={category} rules={list} disabledIds={disabledIds} />
          ))
        )}
      </Section>

      {/* Audit history of skipped rules */}
      {auditLog && auditLog.length > 0 && (
        <Section title="Recent override-driven skips"
          subtitle="Append-only audit trail of every classification rule that was skipped because of this workflow's override config. Compliance source of truth for 'what protections were waived for which run'.">
          <div style={{ background: '#fff', border: `1px solid ${COLORS.S[200]}`, borderRadius: 10, overflow: 'hidden' }}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 0.7fr 1.2fr 0.9fr',
              gap: 10, padding: '9px 14px', background: COLORS.S[50], borderBottom: `1px solid ${COLORS.S[200]}`,
              fontSize: 10, fontWeight: 700, color: COLORS.S[600], textTransform: 'uppercase', letterSpacing: '0.06em',
            }}>
              <div>Rule skipped</div>
              <div>Action</div>
              <div>Direction</div>
              <div>Reason</div>
              <div>When</div>
            </div>
            {auditLog.slice(0, 25).map(a => (
              <div key={a.id} style={{
                padding: '9px 14px', borderBottom: `1px solid ${COLORS.S[150]}`,
                display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 0.7fr 1.2fr 0.9fr', gap: 10,
                fontSize: 12, alignItems: 'center',
              }}>
                <div>
                  <div style={{ fontWeight: 600, color: COLORS.S[900] }}>{a.ruleName}</div>
                  <div style={{ fontSize: 10, color: COLORS.S[500] }}>{a.ruleCategory}</div>
                </div>
                <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', color: ACTION_COLOR[a.ruleAction] || COLORS.S[700], fontWeight: 700 }}>
                  {a.ruleAction}
                </div>
                <div style={{ color: COLORS.S[700] }}>{a.scanDirection || '—'}</div>
                <div style={{ color: COLORS.S[600], overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {a.overrideReason || '(no reason on record)'}
                </div>
                <div style={{ color: COLORS.S[500], fontSize: 11 }}>
                  {a.occurredAt ? new Date(a.occurredAt).toLocaleString() : '—'}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}

function StatCard({ label, value, sub, accent, Icon }) {
  const a = COLORS[accent] || COLORS.sky;
  return (
    <div style={{ background: '#fff', border: `1px solid ${COLORS.S[200]}`, borderRadius: 12,
      padding: '16px 18px', position: 'relative', overflow: 'hidden' }}>
      <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: 3, background: a.icon, opacity: 0.18 }} />
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: a.text, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
          <div style={{ fontSize: 28, fontWeight: 800, color: COLORS.S[900], lineHeight: 1, fontFeatureSettings: "'tnum'" }}>{value}</div>
          <div style={{ fontSize: 11, color: COLORS.S[500], marginTop: 6, lineHeight: 1.4 }}>{sub}</div>
        </div>
        <div style={{ width: 34, height: 34, borderRadius: 10, background: a.bg, border: `1px solid ${a.border}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
          <Icon size={16} color={a.icon} />
        </div>
      </div>
    </div>
  );
}

function Section({ title, subtitle, children }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ marginBottom: 10 }}>
        <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.S[900] }}>{title}</div>
        {subtitle && <div style={{ fontSize: 11, color: COLORS.S[500], marginTop: 2 }}>{subtitle}</div>}
      </div>
      {children}
    </div>
  );
}

function CategoryGroup({ category, rules, disabledIds }) {
  const Icon = CATEGORY_ICON[category] || Zap;
  const accent = COLORS[CATEGORY_ACCENT[category] || 'slate'];
  return (
    <div style={{ marginBottom: 12, background: '#fff', border: `1px solid ${COLORS.S[200]}`, borderRadius: 10, overflow: 'hidden' }}>
      <div style={{ padding: '10px 14px', background: accent.bg, borderBottom: `1px solid ${accent.border}`,
        display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 26, height: 26, borderRadius: 7, background: '#fff',
          border: `1px solid ${accent.border}`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={13} color={accent.icon} />
        </div>
        <div style={{ fontSize: 12, fontWeight: 700, color: accent.text, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
          {category}
        </div>
        <div style={{ fontSize: 11, color: COLORS.S[600], marginLeft: 'auto' }}>
          {rules.length} rule{rules.length !== 1 ? 's' : ''}
        </div>
      </div>
      {rules.map(r => (
        <RuleRowCompact key={r.id} rule={r} disabledByOverride={disabledIds && disabledIds.has(r.id)} />
      ))}
    </div>
  );
}

function RuleRowCompact({ rule, disabledByOverride }) {
  const isSystem = rule.tenantId === SYSTEM_TENANT_ID;
  return (
    <div style={{
      padding: '10px 14px', borderBottom: `1px solid ${COLORS.S[150]}`,
      display: 'grid', gridTemplateColumns: '1.6fr 0.8fr 0.7fr 0.6fr 0.5fr', gap: 10, alignItems: 'center', fontSize: 12,
      opacity: disabledByOverride ? 0.55 : 1,
      background: disabledByOverride ? COLORS.amber.bg : 'transparent',
    }}>
      <div style={{ minWidth: 0 }}>
        <div style={{ fontWeight: 600, color: COLORS.S[900], display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          {rule.name}
          {disabledByOverride && (
            <span style={{
              fontSize: 9, padding: '2px 6px', borderRadius: 4,
              background: COLORS.amber.icon, color: '#fff',
              fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em',
              display: 'inline-flex', alignItems: 'center', gap: 3,
            }}>
              <X size={9} /> DISABLED BY OVERRIDE
            </span>
          )}
          {!rule.enabled && (
            <span style={{ fontSize: 9, padding: '1px 5px', borderRadius: 4, background: COLORS.S[150], color: COLORS.S[600], fontWeight: 700, textTransform: 'uppercase' }}>OFF</span>
          )}
        </div>
        {rule.complianceFramework && (
          <div style={{ fontSize: 10, color: COLORS.sky.text, marginTop: 2, fontWeight: 600 }}>{rule.complianceFramework}</div>
        )}
      </div>
      <div style={{ fontFamily: 'ui-monospace, SFMono-Regular, monospace', color: COLORS.S[700] }}>
        {rule.detectionMethod}
      </div>
      <div>
        <span style={{ fontSize: 11, fontWeight: 700, color: ACTION_COLOR[rule.action] || COLORS.S[700],
          textTransform: 'uppercase', letterSpacing: '0.04em' }}>{rule.action}</span>
      </div>
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
        {isSystem
          ? <><Globe size={11} color={COLORS.slate.icon} /><span style={{ color: COLORS.slate.text, fontSize: 11, fontWeight: 600 }}>System</span></>
          : <><Building2 size={11} color={COLORS.brand.primary} /><span style={{ color: COLORS.brand.primary, fontSize: 11, fontWeight: 600 }}>Tenant</span></>}
      </div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <SensitivityDots level={rule.sensitivityLevel} />
      </div>
    </div>
  );
}

function SensitivityDots({ level }) {
  const lvl = Math.min(5, Math.max(1, level || 1));
  const colors = ['#84cc16', '#a3e635', '#facc15', '#fb923c', '#dc2626'];
  return (
    <div style={{ display: 'flex', gap: 2 }} title={`Sensitivity ${lvl}/5`}>
      {Array.from({ length: 5 }).map((_, i) => (
        <div key={i} style={{
          width: 6, height: 12, borderRadius: 1.5,
          background: i < lvl ? colors[lvl - 1] : COLORS.S[150],
        }} />
      ))}
    </div>
  );
}

/**
 * CustomizeProtectionsSection — the per-workflow override editor.
 *
 * Renders inline (no modal) so the operator can see the rule list above
 * and the override config side-by-side. Read state shows the currently
 * disabled rules + reason; click "Customize" to enter edit mode where
 * checkboxes mark which rules to skip and a textarea captures the
 * justification. Save sends {disabledRuleIds, reason} to the API; the
 * runtime cache is invalidated so the next workflow run picks up the
 * change immediately.
 *
 * Hidden / read-only when:
 *   - canManage === false (gated by Teams page MANAGE level)
 *   - the workflow's effective rule set is empty (nothing to override)
 */
function CustomizeProtectionsSection({ workflowId, rules, overrides, canManage, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [draftIds, setDraftIds] = useState(new Set());
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const currentDisabled = useMemo(() => new Set(overrides?.disabledRuleIds || []), [overrides]);
  const currentReason = overrides?.reason || '';

  // Reset draft when overrides reload (after save)
  React.useEffect(() => {
    setDraftIds(new Set(overrides?.disabledRuleIds || []));
    setReason(overrides?.reason || '');
  }, [overrides]);

  const dirty = useMemo(() => {
    if (reason !== currentReason) return true;
    if (draftIds.size !== currentDisabled.size) return true;
    for (const id of draftIds) if (!currentDisabled.has(id)) return true;
    return false;
  }, [draftIds, currentDisabled, reason, currentReason]);

  const toggle = (id) => {
    const next = new Set(draftIds);
    if (next.has(id)) next.delete(id); else next.add(id);
    setDraftIds(next);
  };

  const handleSave = async () => {
    if (!reason.trim() && draftIds.size > 0) {
      setError('A justification reason is required when disabling rules — this is the audit trail compliance will read.');
      return;
    }
    setSaving(true); setError(null);
    try {
      const res = await api.setWorkflowDpgOverrides(workflowId, {
        disabledRuleIds: Array.from(draftIds),
        reason: reason.trim() || null,
      });
      if (res.ok) {
        setEditing(false);
        if (onSaved) await onSaved();
      } else {
        setError(res.data?.message || 'Save failed');
      }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  const handleClear = async () => {
    if (!window.confirm('Clear all DPG overrides for this workflow? Every classification rule will fire again on the next run.')) return;
    setSaving(true); setError(null);
    try {
      const res = await api.clearWorkflowDpgOverrides(workflowId);
      if (res.ok) {
        setEditing(false);
        if (onSaved) await onSaved();
      } else {
        setError(res.data?.message || 'Clear failed');
      }
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  if (rules.length === 0) return null;
  const disabledCount = currentDisabled.size;

  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{ marginBottom: 10, display: 'flex', alignItems: 'center', gap: 10 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.S[900], display: 'flex', alignItems: 'center', gap: 8 }}>
            <Sliders size={14} color={COLORS.brand.primary} /> Customize protections
            {disabledCount > 0 && (
              <span style={{ fontSize: 10, padding: '2px 8px', borderRadius: 999,
                background: COLORS.amber.bg, color: COLORS.amber.text, border: `1px solid ${COLORS.amber.border}`,
                fontWeight: 700 }}>
                {disabledCount} rule{disabledCount !== 1 ? 's' : ''} disabled
              </span>
            )}
          </div>
          <div style={{ fontSize: 11, color: COLORS.S[500], marginTop: 2 }}>
            {canManage
              ? 'Tick a rule to skip it for this workflow only. The runtime audit table records every skipped scan with the reason below.'
              : 'View only — MANAGE access (set on the Teams page) is required to edit overrides.'}
          </div>
        </div>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {!editing && canManage && (
            <button onClick={() => setEditing(true)}
              style={btn(COLORS.brand.primary, '#fff')}>
              <Sliders size={14} /> {disabledCount > 0 ? 'Edit overrides' : 'Customize'}
            </button>
          )}
          {!editing && canManage && disabledCount > 0 && (
            <button onClick={handleClear} disabled={saving}
              style={btn('#fff', COLORS.S[700], COLORS.S[200])}>
              <RotateCcw size={13} /> Clear all
            </button>
          )}
          {editing && (
            <>
              <button onClick={() => { setEditing(false); setError(null); setDraftIds(new Set(overrides?.disabledRuleIds || [])); setReason(overrides?.reason || ''); }}
                disabled={saving}
                style={btn('#fff', COLORS.S[700], COLORS.S[200])}>
                <X size={13} /> Cancel
              </button>
              <button onClick={handleSave} disabled={!dirty || saving}
                style={{ ...btn(COLORS.brand.primary, '#fff'), opacity: !dirty || saving ? 0.5 : 1 }}>
                {saving ? <Loader size={13} style={{ animation: 'wfpSpin 0.8s linear infinite' }} /> : <Save size={13} />}
                Save overrides
              </button>
            </>
          )}
        </div>
      </div>

      {error && (
        <div style={{ marginBottom: 12, padding: '10px 14px', borderRadius: 8,
          background: COLORS.rose.bg, border: `1px solid ${COLORS.rose.border}`,
          color: COLORS.rose.text, fontSize: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
          <AlertTriangle size={14} color={COLORS.rose.icon} /> {error}
        </div>
      )}

      {/* Read mode */}
      {!editing && (
        <div style={{ background: '#fff', border: `1px solid ${COLORS.S[200]}`, borderRadius: 10, padding: '14px 16px' }}>
          {disabledCount === 0 ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: COLORS.S[600], fontSize: 13 }}>
              <CheckCircle size={16} color={COLORS.emerald.icon} />
              <span>No overrides — every enabled tenant rule fires on this workflow.</span>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 10 }}>
                {Array.from(currentDisabled).map(id => {
                  const r = rules.find(x => x.id === id);
                  if (!r) return (
                    <span key={id} style={chip(COLORS.S[100], COLORS.S[600], COLORS.S[200])}>
                      Unknown rule {id.slice(0, 8)}…
                    </span>
                  );
                  return (
                    <span key={id} style={chip(COLORS.amber.bg, COLORS.amber.text, COLORS.amber.border)}>
                      <X size={11} /> {r.name}
                    </span>
                  );
                })}
              </div>
              {currentReason && (
                <div style={{ fontSize: 12, color: COLORS.S[700], marginTop: 4, paddingTop: 10,
                  borderTop: `1px solid ${COLORS.S[150]}` }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: COLORS.S[500], textTransform: 'uppercase', letterSpacing: '0.06em' }}>Reason:</span>
                  <div style={{ marginTop: 4, fontStyle: 'italic' }}>{currentReason}</div>
                </div>
              )}
              {overrides?.lastUpdatedBy && overrides?.lastUpdatedAt && (
                <div style={{ fontSize: 10, color: COLORS.S[500], marginTop: 8 }}>
                  Last updated {new Date(overrides.lastUpdatedAt).toLocaleString()} by {overrides.lastUpdatedBy.slice(0, 8)}…
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Edit mode */}
      {editing && (
        <div style={{ background: '#fff', border: `2px solid ${COLORS.brand.primary}`, borderRadius: 10, padding: '14px 16px' }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.S[600], textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 8 }}>
            Tick rules to disable for this workflow
          </div>
          <div style={{ maxHeight: 280, overflowY: 'auto', border: `1px solid ${COLORS.S[200]}`, borderRadius: 8, marginBottom: 12 }}>
            {rules.map(r => {
              const checked = draftIds.has(r.id);
              return (
                <label key={r.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 10, padding: '9px 12px',
                  borderBottom: `1px solid ${COLORS.S[150]}`, cursor: 'pointer',
                  background: checked ? COLORS.amber.bg : 'transparent',
                }}>
                  <input type="checkbox" checked={checked} onChange={() => toggle(r.id)} style={{ marginTop: 2 }} />
                  <div style={{ flex: 1, minWidth: 0, fontSize: 12 }}>
                    <div style={{ fontWeight: 600, color: COLORS.S[900], display: 'flex', alignItems: 'center', gap: 6 }}>
                      {r.name}
                      <span style={{ fontSize: 10, padding: '1px 6px', borderRadius: 4, background: COLORS.S[100], color: COLORS.S[700], fontFamily: 'ui-monospace, SFMono-Regular, monospace' }}>
                        {r.category}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 700, color: ACTION_COLOR[r.action] || COLORS.S[700] }}>{r.action}</span>
                    </div>
                    {r.description && (
                      <div style={{ fontSize: 11, color: COLORS.S[500], marginTop: 2 }}>{r.description}</div>
                    )}
                  </div>
                </label>
              );
            })}
          </div>
          <div>
            <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.S[600], textTransform: 'uppercase',
              letterSpacing: '0.06em', display: 'block', marginBottom: 6 }}>
              Justification (required when disabling rules)
              <span style={{ color: COLORS.brand.primary, marginLeft: 4 }}>*</span>
            </label>
            <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
              placeholder="Why are these scans being skipped on this workflow? This text is recorded in the override audit trail for every rule skip."
              style={{ width: '100%', padding: '9px 12px', border: `1px solid ${COLORS.S[200]}`, borderRadius: 8,
                fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box', resize: 'vertical' }} />
          </div>
        </div>
      )}
    </div>
  );
}

function btn(bg, color, border = null) {
  return {
    background: bg, color, border: border ? `1px solid ${border}` : 'none',
    cursor: 'pointer', borderRadius: 8, padding: '7px 12px', fontSize: 12, fontWeight: 600,
    display: 'inline-flex', alignItems: 'center', gap: 5,
  };
}
function chip(bg, color, border) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    padding: '4px 10px', borderRadius: 999, background: bg, color, border: `1px solid ${border}`,
    fontSize: 11, fontWeight: 600,
  };
}
